/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo, useCallback } from 'react';
import { isEmpty, map, prop, complement } from 'ramda';
import { useDropzone } from 'react-dropzone';
import { parse } from 'papaparse';

import { useToast } from '@shared';
import { Container } from './DropZone.styled';
import { normalizeCsv } from '../helpers';

const notEmpty = complement(isEmpty);

const defaultMaxSize = 1e6; // 1 Mb
const defaultAccepted = ['.csv', 'text/csv'];

interface Props {
  onUpload: (files: File[]) => void;
  setParsedData: (values: string[][]) => void;
  children?: JSX.Element;
  fileTypes?: string[];
  maxSize?: number;
  noClick?: boolean;
  noKeyboard?: boolean;
  multiple?: boolean;
  disabled?: boolean;
  hideErrors?: boolean;
}

export function FilesUpload({
  onUpload,
  setParsedData,
  children,
  maxSize = defaultMaxSize,
  fileTypes = defaultAccepted,
  noClick = false,
  noKeyboard = true,
  multiple = false,
  disabled = false,
  hideErrors = false,
}: Props) {
  const toast = useToast();

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          if (reader.result) {
            parse(reader.result as any, {
              header: false,
              complete: (result) => {
                if (result.data.length > 151) {
                  toast.error('The limit of 150 entries has been exceeded');
                } else {
                  const resultData = normalizeCsv(result.data as string[][]);
                  setParsedData(resultData);
                  onUpload(acceptedFiles);
                }
              },
              error: (error: any) => {
                console.error('Error parsing CSV:', error);
              },
            });
          }
        };
        reader.readAsText(file);
      });
    },
    [onUpload, setParsedData, toast]
  );

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, fileRejections } =
    useDropzone({
      accept: { fileTypes },
      noClick,
      noKeyboard,
      maxSize,
      multiple,
      onDrop,
      disabled,
    });

  const rejectedFiles = useMemo(() => {
    if (notEmpty(fileRejections)) {
      return map(({ file }) => prop('name', file), fileRejections);
    }
    return '';
  }, [fileRejections]);

  useEffect(() => {
    if (rejectedFiles && !hideErrors) {
      toast.error('Invalid file format or max file size exceeded.');
    }
  }, [hideErrors, rejectedFiles, toast]);

  return (
    <Container {...getRootProps({ isDragActive, isDragAccept, isDragReject })}>
      <>
        <input {...getInputProps()} />
        {children}
      </>
    </Container>
  );
}
